import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";

const lines = [{ text: "ANXIOUS" }, { text: "BEHIND THE" }, { text: "WHEEL?", accent: true }];

const stats = [
  ["8PM–3AM", "Odd Hour Lessons"],
  ["30 MIN", "Free parent & learner chat"],
  ["100%", "Patient & supportive"],
];

export default function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 120]);

  return (
    <section id="top" className="relative min-h-screen flex flex-col justify-center px-6 md:px-12 pt-36 pb-20 max-w-[1600px] mx-auto">
      <div className="grid lg:grid-cols-[1.35fr_1fr] gap-16 items-center">
        <div>
          <motion.p
            data-testid="hero-eyebrow"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-sm font-bold uppercase tracking-[0.3em] text-[#F5B301] mb-10"
          >
            Real Life Learners — Coolum to Caloundra
          </motion.p>
          <h1 className="font-cabinet font-black uppercase tracking-tighter leading-[0.9] text-[14vw] lg:text-[7vw] text-[#f5f5f0]">
            {lines.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.accent ? "text-[#F5B301]" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1], delay: 0.3 + i * 0.12 }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24, rotate: -2 }}
            animate={{ opacity: 1, y: 0, rotate: -2 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="font-script text-[#F5B301] text-5xl md:text-7xl mt-4"
            data-testid="hero-script-line"
          >
            You're not alone.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="text-base md:text-lg text-white/60 max-w-md mt-8 leading-relaxed"
          >
            We help nervous drivers build real confidence in a calm, patient and supportive environment — including unique Odd Hour Lessons from 8PM to 3AM. Quieter roads. Less stress. More confidence.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.15 }}
            className="flex flex-wrap items-center gap-4 mt-10"
          >
            <a
              href="#book"
              data-testid="hero-book-btn"
              className="group inline-flex items-center gap-3 bg-[#F5B301] text-[#0a0a0a] font-bold uppercase tracking-wide text-sm px-8 py-4 hover:bg-[#f5f5f0] transition-colors duration-300"
            >
              Book Your First Lesson
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="sms:0427779709"
              data-testid="hero-text-cam-btn"
              className="inline-flex items-center gap-3 border-2 border-[#f5f5f0] text-[#f5f5f0] font-bold uppercase tracking-wide text-sm px-8 py-4 hover:bg-[#f5f5f0] hover:text-[#0a0a0a] transition-colors duration-300"
            >
              Text Cam — 0427 779 709
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: 0.5 }}
          className="relative hidden lg:block"
        >
          <div className="absolute -top-4 -left-4 w-full h-full border-2 border-[#F5B301]" />
          <div className="relative overflow-hidden aspect-[3/4]">
            <motion.video
              src="/media/car.mp4"
              poster="/media/car-poster.jpg"
              autoPlay
              muted
              loop
              playsInline
              data-testid="hero-video"
              style={{ y }}
              className="w-full h-[125%] object-cover -mt-[8%]"
            />
          </div>
          <div className="absolute -bottom-6 -left-10 bg-[#F5B301] text-[#0a0a0a] px-6 py-4" data-testid="hero-passrate-badge">
            <p className="font-cabinet font-black text-3xl leading-none">8PM–3AM</p>
            <p className="text-xs uppercase tracking-widest mt-1 text-black/70">Odd Hour Lessons</p>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.3 }}
        className="grid grid-cols-3 gap-6 border-t border-white/10 mt-20 pt-8"
      >
        {stats.map(([value, label]) => (
          <div key={label} data-testid={`hero-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <p className="font-cabinet font-black text-2xl md:text-5xl tracking-tighter text-[#f5f5f0]">{value}</p>
            <p className="text-xs md:text-sm uppercase tracking-widest text-white/50 mt-1">{label}</p>
          </div>
        ))}
      </motion.div>
    </section>
  );
}
